import { themeVariables } from "Components/theme";
import { Arrow, BendAnchoredPortLocationModel, EdgeStyleBase, GeneralPath, GraphComponent, IArrow, ISelectionIndicatorInstaller, NodeStylePortStyleAdapter, PolylineEdgeStyle, ShapeNodeStyle, SimplePort, SvgVisual, SvgVisualGroup } from "yfiles";
import CustomSelectionInstaller from "./customEdgeSelectionInstaller";
import EdgeRenderDataCache from "./edgeRenderDataCache";
const SIZES = {
    BASE_PATH_THICKNESS: 2,
    BEND_SIZE: 16
};
const helperEdgeStyle = new PolylineEdgeStyle({
    sourceArrow: IArrow.NONE,
    targetArrow: IArrow.NONE
});
const calculateColor = (color, selected) => color || themeVariables[selected ? "brand-primary-500" : "gray-400"];
const calculateStrokeWidth = (color, selected) => `${!selected
    ? SIZES.BASE_PATH_THICKNESS
    : color
        ? SIZES.BASE_PATH_THICKNESS + 3
        : SIZES.BASE_PATH_THICKNESS + 2}px`;
const buildEdgeTargetArrow = (color, selected) => {
    const resultColor = calculateColor(color, selected);
    return new Arrow({
        fill: resultColor,
        scale: 1,
        type: "triangle",
        stroke: `${selected ? 2.8 : 1.4}px ${resultColor}`,
        cropLength: 2
    });
};
const basePortStyle = new NodeStylePortStyleAdapter({
    nodeStyle: new ShapeNodeStyle({
        fill: "transparent",
        stroke: null,
        shape: "ellipse"
    }),
    renderSize: [SIZES.BEND_SIZE, SIZES.BEND_SIZE]
});
export default class CustomEdgeStyle extends EdgeStyleBase {
    createVisual(context, edge) {
        const group = new SvgVisualGroup();
        const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
        group.add(SvgVisual.from(g));
        const cache = this.createRenderDataCache(context, edge);
        const visual = SvgVisual.from(g, cache);
        this.render(context, visual, edge);
        const bendGroup = new SvgVisualGroup();
        group.add(bendGroup);
        CustomEdgeStyle.renderBends(context, bendGroup, edge, cache.selected);
        return group;
    }
    updateVisual(context, oldVisual, edge) {
        if (!(oldVisual instanceof SvgVisualGroup) || oldVisual.children.size !== 2) {
            return this.createVisual(context, edge);
        }
        const baseVisual = oldVisual.children.get(0);
        const bendGroup = oldVisual.children.get(1);
        const container = baseVisual.svgElement;
        const oldCache = baseVisual.tag;
        const newCache = this.createRenderDataCache(context, edge);
        if (!newCache.stateEquals(oldCache)) {
            CustomEdgeStyle.clear(container);
            baseVisual.tag = newCache;
            this.render(context, baseVisual, edge);
            CustomEdgeStyle.renderBends(context, bendGroup, edge, newCache.selected);
            return oldVisual;
        }
        if (!newCache.pathEquals(oldCache)) {
            baseVisual.tag = newCache;
            this.updatePath(context, baseVisual, edge);
            CustomEdgeStyle.renderBends(context, bendGroup, edge, newCache.selected);
        }
        return oldVisual;
    }
    createRenderDataCache(context, edge) {
        var _a;
        return new EdgeRenderDataCache(CustomEdgeStyle.isSelected(context, edge), (_a = edge.tag) === null || _a === void 0 ? void 0 : _a.Color, this.getPath(edge));
    }
    render(context, visual, edge) {
        var _a, _b, _c;
        const cache = visual.tag;
        const container = visual.svgElement;
        if (!cache.path)
            return;
        const path = cache.path.createSvgPath();
        path.setAttribute("fill", "none");
        path.setAttribute("stroke-width", calculateStrokeWidth((_a = edge.tag) === null || _a === void 0 ? void 0 : _a.Color, cache.selected));
        path.setAttribute("stroke-linejoin", "round");
        path.setAttribute("stroke", calculateColor((_b = edge.tag) === null || _b === void 0 ? void 0 : _b.Color, cache.selected));
        container.appendChild(path);
        super.addArrows(context, container, edge, cache.path, IArrow.NONE, buildEdgeTargetArrow((_c = edge.tag) === null || _c === void 0 ? void 0 : _c.Color, cache.selected));
    }
    updatePath(context, oldVisual, edge) {
        var _a;
        const container = oldVisual.svgElement;
        if (container.childNodes.length === 0 || !(container.childNodes[0] instanceof SVGPathElement)) {
            CustomEdgeStyle.clear(container);
            this.render(context, oldVisual, edge);
            return;
        }
        const cache = oldVisual.tag;
        if (cache.path) {
            const path = container.childNodes[0];
            const updatedPath = cache.path.createSvgPath();
            path.setAttribute("d", updatedPath.getAttribute("d"));
            super.updateArrows(context, container, edge, cache.path, IArrow.NONE, buildEdgeTargetArrow((_a = edge.tag) === null || _a === void 0 ? void 0 : _a.Color, cache.selected));
        }
        else {
            CustomEdgeStyle.clear(container);
        }
    }
    getPath(edge) {
        var _a;
        const path = new GeneralPath();
        path.moveTo(edge.sourcePort.location);
        edge.bends.forEach(bend => {
            path.lineTo(bend.location);
        });
        path.lineTo(edge.targetPort.location);
        return super.cropPath(edge, IArrow.NONE, buildEdgeTargetArrow((_a = edge.tag) === null || _a === void 0 ? void 0 : _a.Color, false), path);
    }
    isHit(context, location, edge) {
        const path = this.getPath(edge);
        return (!!path && path.pathContains(location, context.hitTestRadius + SIZES.BASE_PATH_THICKNESS * 0.5));
    }
    isVisible(context, rectangle, edge) {
        const enlargedRectangle = rectangle.getEnlarged(SIZES.BASE_PATH_THICKNESS);
        return helperEdgeStyle.renderer
            .getVisibilityTestable(edge, helperEdgeStyle)
            .isVisible(context, enlargedRectangle);
    }
    lookup(edge, type) {
        return type === ISelectionIndicatorInstaller.$class
            ? new CustomSelectionInstaller()
            : super.lookup(edge, type);
    }
    static clear(container) {
        while (container.lastChild) {
            container.removeChild(container.lastChild);
        }
    }
    static isSelected(context, edge) {
        const component = context.canvasComponent;
        return component instanceof GraphComponent && component.selection.isSelected(edge);
    }
    static renderBends(context, group, edge, edgeSelected) {
        if (!edgeSelected) {
            CustomEdgeStyle.clear(group.svgElement);
            return;
        }
        const { bends, tag } = edge;
        while (group.children.size > bends.size) {
            group.remove(group.children.get(group.children.size - 1));
        }
        const bendStyle = basePortStyle.clone();
        bendStyle.nodeStyle.fill = tag.Color || themeVariables["brand-primary-500"];
        const dummyPort = new SimplePort(edge);
        const portLocationModel = BendAnchoredPortLocationModel.INSTANCE;
        for (let i = 0; i < group.children.size; i++) {
            dummyPort.locationParameter = portLocationModel.createFromSource(i - 1);
            const visual = basePortStyle.renderer
                .getVisualCreator(dummyPort, basePortStyle)
                .updateVisual(context, group.children.get(i));
            if (group.children.get(i) !== visual) {
                group.children.set(i, visual);
            }
        }
        for (let i = group.children.size; i < bends.size; i++) {
            dummyPort.locationParameter = portLocationModel.createFromSource(i - 1);
            const bendVisual = bendStyle.renderer
                .getVisualCreator(dummyPort, bendStyle)
                .createVisual(context);
            group.children.add(bendVisual);
        }
    }
}
